/**
 * PATH: apps/qa/src/pages/NCRDetailPage.tsx
 * PURPOSE: View, create, and manage Non-Conformance Records (orchestrator)
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 *   │ Fetch/Create│───▶│ Add Actions  │───▶│ Resolve     │
 *   │ NCR         │    │ & Notes      │    │ & Close     │
 *   └─────────────┘    └──────────────┘    └─────────────┘
 *
 * DEPENDENCIES:
 *   - ncr-detail/helpers: Status/severity config
 *   - ncr-detail/NCRCreateForm: New NCR creation flow
 *   - ncr-detail/NCRActivityPanel: Activity timeline
 *   - ncr-detail/NCRDetailsSidebar: Details + status transitions
 */

import { useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ncrApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ArrowLeft, AlertTriangle, Loader2 } from 'lucide-react'
import { getStatusConfig, getSeverityConfig } from './ncr-detail/helpers'
import { NCRCreateForm } from './ncr-detail/NCRCreateForm'
import { NCRActivityPanel } from './ncr-detail/NCRActivityPanel'
import { NCRDetailsSidebar } from './ncr-detail/NCRDetailsSidebar'
import { useQaTabStore } from '../config/tabs'

export default function NCRDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const updateActiveTab = useQaTabStore(s => s.updateActiveTab)
  const isNew = !id || id === 'new'

  const { data: ncr, isLoading, error } = useQuery({
    queryKey: ['qa-ncr', id],
    queryFn: () => ncrApi.getNCR(id!),
    enabled: !isNew && !!id,
  })

  useEffect(() => {
    if (ncr?.title || ncr?.id) {
      updateActiveTab(location.pathname, ncr?.title || `NCR #${id?.slice(0, 8)}`)
    }
  }, [ncr?.title, ncr?.id, id, location.pathname, updateActiveTab])

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    )
  }

  if (!isNew && error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-2xl bg-destructive/10 mx-auto mb-4 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>
        <p className="text-foreground font-medium">NCR not found</p>
        <Link to="/ncrs" className="mt-4 inline-flex items-center gap-2 text-primary hover:text-primary-600 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to NCRs
        </Link>
      </div>
    )
  }

  if (isNew) return <NCRCreateForm />

  const statusConfig = getStatusConfig(ncr?.status || 'open')
  const sevConfig = getSeverityConfig(ncr?.severity || 'minor')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/ncrs')} className="p-2 rounded-lg hover:bg-accent transition-colors">
            <ArrowLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">NCR #{ncr?.id?.slice(0, 8) || 'Details'}</h1>
              <span className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium capitalize', sevConfig.bg, sevConfig.text)}>
                {ncr?.severity}
              </span>
            </div>
            <p className="text-muted-foreground">{ncr?.title}</p>
          </div>
        </div>
        <span className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium', statusConfig.bg, statusConfig.text)}>
          {statusConfig.icon} {statusConfig.label}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Description</h2>
            <p className="text-foreground whitespace-pre-wrap">
              {ncr?.issue_description || ncr?.description || 'No description provided.'}
            </p>
          </div>

          {/* Activity */}
          <NCRActivityPanel ncrId={id!} actions={ncr?.actions || []} status={ncr?.status || 'open'} />
        </div>

        {/* Sidebar */}
        <NCRDetailsSidebar ncr={ncr || {}} />
      </div>
    </div>
  )
}
